/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { simpleFetch } from '../../../restWrapper';
import PayoutReceipts, { payoutReceiptText } from './PayoutReceipts';

export default function PayoutHistoryTable({ list, textObj, language }) {
  const [receiptStates, setReceiptStates] = useState({});
  const referenceKey = JSON.stringify([...new Set(list.map(payout => payout.id))]);
  useEffect(() => {
    let active = true;
    const references = JSON.parse(referenceKey);
    setReceiptStates({});
    const load = async () => {
      // One read-only batch per page (chunked at the server's 20-payout limit),
      // not a state-changing sync from every receipt cell.
      for (let index = 0; index < references.length; index += 20) {
        if (!active) return;
        const batch = references.slice(index, index + 20);
        let states;
        try {
          const res = await simpleFetch('/organizations/payouts/reconciliation/stripe/receipts', 'GET', { references: batch.join(',') });
          if (res.status !== 200) throw new Error(res.body?.message || 'Receipts unavailable.');
          states = Object.fromEntries(batch.map(reference => {
            const payout = res.body?.[reference];
            return [reference, payout && !payout.error ? { loading: false, payout } :
              { loading: false, error: payout?.error || 'Receipts unavailable.' }];
          }));
        } catch (error) {
          states = Object.fromEntries(batch.map(reference => [reference, { loading: false, error: error.message }]));
        }
        if (active) setReceiptStates(previous => ({ ...previous, ...states }));
      }
    };
    load();
    return () => { active = false; };
  }, [referenceKey]);
  const columns = ['amount', 'currency', 'status', 'bank', 'created', 'arrival_date'];
  const date = value => typeof value === 'number' ? moment(new Date(value * 1000)).format('MMMM DD, YYYY h:mma') : value || '—';
  return <div className="OrgPayout-gridBG">
    <table className="OrgPayout-historyTable" aria-label={textObj.heading[2]}>
      <thead><tr>{columns.map(key => <th scope="col" key={key}>{textObj.gridTitle[key]}</th>)}<th scope="col">{payoutReceiptText(language).column}</th></tr></thead>
      <tbody>{list.map(payout => <tr key={payout.id}>
        <td>{typeof payout.amount === 'number' ? 'HK$' + payout.amount / 100 : payout.amount}</td>
        <td>{payout.currency?.toUpperCase()}</td>
        <td>{payout.reversed_by ? 'reversed' : payout.status}</td>
        <td>{[payout.bankData?.bank_name, payout.bankData?.last4].filter(Boolean).join(' ') || '—'}</td>
        <td>{date(payout.created)}</td>
        <td>{date(payout.arrival_date)}</td>
        <td><PayoutReceipts receiptState={receiptStates[payout.id]} language={language} /></td>
      </tr>)}</tbody>
    </table>
  </div>;
}
PayoutHistoryTable.propTypes = { list: PropTypes.array.isRequired, textObj: PropTypes.object.isRequired, language: PropTypes.string };
